import { useState, useCallback } from "react";
import { GridRowId, GridRowSelectionModel } from "@mui/x-data-grid-premium";

interface SnackbarState {
  open: boolean;
  message: string;
  severity: "success" | "error";
}

interface UseCreateFormModalOptions {
  apiRef: React.RefObject<any>;
  rowSelectionModel: GridRowSelectionModel;
  setSnackbar: (snackbar: SnackbarState) => void;
  onRefresh: () => void;
  onSuccess?: (message: string) => void;
  onError?: (error: Error) => void;
  onCreateClick?: () => void;
  onDuplicateClick?: (selectedRow: any) => void;
  flattenDuplicateData: (data: Record<string, any> | null) => Record<string, any> | null;
}

interface UseCreateFormModalReturn {
  createModalOpen: boolean;
  setCreateModalOpen: (open: boolean) => void;
  duplicateData: Record<string, any> | null;
  handleAddClick: () => void;
  handleDuplicateClick: () => void;
  handleCreateSuccess: (message: string) => void;
  handleCreateError: (error: Error) => void;
}

/**
 * Hook for managing create/duplicate form modal state and handlers.
 */
export function useCreateFormModal({
  apiRef,
  rowSelectionModel,
  setSnackbar,
  onRefresh,
  onSuccess,
  onError,
  onCreateClick,
  onDuplicateClick,
  flattenDuplicateData,
}: UseCreateFormModalOptions): UseCreateFormModalReturn {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [duplicateData, setDuplicateData] = useState<Record<string, any> | null>(null);

  const handleAddClick = useCallback(() => {
    if (onCreateClick) {
      onCreateClick();
    } else {
      setDuplicateData(null);
      setCreateModalOpen(true);
    }
  }, [onCreateClick]);

  const handleDuplicateClick = useCallback(() => {
    const selectedIds = Array.from(rowSelectionModel.ids);

    if (selectedIds.length === 1) {
      const selectedRow = apiRef.current?.getRow(selectedIds[0]);
      if (selectedRow) {
        if (onDuplicateClick) {
          onDuplicateClick(selectedRow);
        } else {
          // Transform nested structure to flat dot-notation format
          const flattenedData = flattenDuplicateData(selectedRow);
          setDuplicateData(flattenedData);
          setCreateModalOpen(true);
        }
      }
    }
  }, [rowSelectionModel.ids, apiRef, onDuplicateClick, flattenDuplicateData]);

  const handleCreateSuccess = useCallback(
    (message: string) => {
      setSnackbar({
        open: true,
        message,
        severity: "success",
      });
      if (onSuccess) {
        onSuccess(message);
      }
      onRefresh();
    },
    [setSnackbar, onSuccess, onRefresh]
  );

  const handleCreateError = useCallback(
    (error: Error) => {
      setSnackbar({
        open: true,
        message: error.message,
        severity: "error",
      });
      if (onError) {
        onError(error);
      }
    },
    [setSnackbar, onError]
  );

  return {
    createModalOpen,
    setCreateModalOpen,
    duplicateData,
    handleAddClick,
    handleDuplicateClick,
    handleCreateSuccess,
    handleCreateError,
  };
}
